import { Injectable } from '@angular/core';
import { PermissionType, Permission, MemberUserPermission } from '../../entities/security';
import { Settings } from '../../settings';
import { MemberUser } from '../../entites/member-user';

import { MemberUserService } from '../http/member-user.service';
import { MemberUserPermissionService } from '../http/member-user-permission.service';

@Injectable()
export class SecurityService{

    //First level is the fiscalYearId, second is the orgNodeId
    private securityCache:Map<number, Map<string, MemberUserPermission[]>> = new Map<number, Map<string, MemberUserPermission[]>>();

    //How's this for using generics?  This is a two dimensional map of Promises which return an array of Permissions for a user.
    //The key for the first dimension is the fiscalYearId and the second is the orgNodeId.  This has got to be an antipattern :)
    private securityPromises:Map<number, Map<string, Promise<MemberUserPermission[]>>> = new Map<number, Map<string, Promise<MemberUserPermission[]>>>();

    constructor(public memberUserService:MemberUserService, public memberUserPermissionService:MemberUserPermissionService) {

    }

    private getMemberUserPermissions(fiscalYearId:number, orgNodeId:string):Promise<MemberUserPermission[]>{
        //This shouldn't be called again if the value already exists, but if it does just return the value and don't
        //try to get it again
        if(this.securityCache[fiscalYearId] && this.securityCache[fiscalYearId][orgNodeId]){
            return Promise.resolve().then( () =>{
                return this.securityCache[fiscalYearId][orgNodeId];
            });
        }

        //If the call has already been made but the result has not yet been received, return the call's promise
        //instead of making the call again
        if(this.securityPromises[fiscalYearId] && this.securityPromises[fiscalYearId][orgNodeId]){
            return this.securityPromises[fiscalYearId][orgNodeId];
        }

        let memberUserPermissionPromise:Promise<MemberUserPermission[]>;
        
        //Already received the current user
        if(this.memberUserService.currentUser){
            memberUserPermissionPromise = this.getMemberUserPermissionPromise(fiscalYearId, orgNodeId);
        
        //Still need to get the current user
        }else{
            memberUserPermissionPromise = this.memberUserService
                .getMe()
                .then(currentUser => {
                    return this.getMemberUserPermissionPromise(fiscalYearId, orgNodeId);
                });
        }

        if(!this.securityPromises[fiscalYearId]){
            this.securityPromises[fiscalYearId] = new Map<string, Promise<MemberUserPermission[]>>();
        }

        this.securityPromises[fiscalYearId][orgNodeId] = memberUserPermissionPromise;

        return memberUserPermissionPromise;
    }

    private getMemberUserPermissionPromise(fiscalYearId:number, orgNodeId:string):Promise<MemberUserPermission[]>{
        return this.memberUserPermissionService
                .getPermissionsForMember(this.memberUserService.currentUser.MemberUserId, fiscalYearId, orgNodeId)
                .then(
                    memberUserPermissions => {
                        if(!this.securityCache[fiscalYearId]){
                            this.securityCache[fiscalYearId] = new Map<string, MemberUserPermission[]>();
                        }

                        this.securityCache[fiscalYearId][orgNodeId] = memberUserPermissions;

                        return memberUserPermissions;
                    }
                );
    }

    public userCanView(fiscalYearId:number, orgNodeId:string, permission:number):boolean{
        return this.userHasPermission(fiscalYearId, orgNodeId, permission, PermissionType.VIEW);
    }

    public userCanEdit(fiscalYearId:number, orgNodeId:string, permission:number):boolean{
        return this.userHasPermission(fiscalYearId, orgNodeId, permission, PermissionType.EDIT);
    }

    public userCanViewDrilldown(fiscalYearId:number, orgNodeId:string, permission:number):boolean{
        return this.userHasPermission(fiscalYearId, orgNodeId, permission, PermissionType.DRILLDOWN);
    }

    protected userHasPermission(fiscalYearId:number, orgNodeId:string, permission:number, permissionType:number){
        //Feature flag for security.  If security is disabled, allow all
        if(!Settings.securityEnabled){
            return true;
        }

        //If either parameter is null or undefined, no point in making the call.  Just return false
        if(!fiscalYearId || !orgNodeId){
            return false;
        }

        if(!this.securityCache[fiscalYearId] || !this.securityCache[fiscalYearId][orgNodeId]){
            
            this.getMemberUserPermissions(fiscalYearId, orgNodeId);

            return false;
        }

        let memberUserPermission:MemberUserPermission = this.securityCache[fiscalYearId][orgNodeId].getItemByProperty("PermissionId", permission);

        if(!memberUserPermission){
            return false;
        }

        //This is a heuristic, but it works for now.  If we add another permission type it may fail
        return memberUserPermission.PermissionTypeId >= permissionType;
    }
}


// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/ng-router-loader?{"loader":"async-system","genDir":"compiled","aot":false}!./src/app/services/security/security.service.ts